import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadConversation } from "@redux/Hermes/hermes.action";
import { pollTimedOut, pollFailed } from "@redux/Hermes/hermesSlice";
import type { AppDispatch, HermesRootState } from "@containers/Hermes/types";

// A response can finish generating in the background even after the client
// disconnects, so a conversation may be mid-generation when we load it (reload,
// another tab, or a send that hit 409). While `status === "awaiting_response"`,
// re-fetch GET /messages on an interval; loadConversation flips status to "idle"
// as soon as the assistant row has content, which tears this effect down.
const POLL_INTERVAL_MS = 5000;
// Backend releases its stale streaming lock after 10 minutes; give it a couple
// more before declaring the worker dead so we don't error out a live response.
const POLL_TIMEOUT_MS = 12 * 60 * 1000;
// A single failed fetch is likely transient (blip, one 5xx) and the next tick
// retries. Repeated back-to-back failures mean the load can't succeed (deleted
// conversation, expired session, sustained outage) — surface it instead of
// spinning for the full timeout.
const MAX_CONSECUTIVE_FAILURES = 3;

export default function useHermesPolling(): void {
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((state: HermesRootState) => state.hermes.status);
  const pollingConversationId = useSelector((state: HermesRootState) => state.hermes.pollingConversationId);
  // { id, at } so the 12-min ceiling is anchored to when we began waiting on
  // THIS conversation, not reset every time the effect re-runs (a navigation
  // blip must not defer the timeout).
  const startedAtRef = useRef<{ id: string; at: number } | null>(null);
  const failuresRef = useRef(0);

  useEffect(() => {
    if (status !== "awaiting_response" || !pollingConversationId) {
      startedAtRef.current = null;
      failuresRef.current = 0;
      return;
    }

    const conversationId = pollingConversationId;
    if (!startedAtRef.current || startedAtRef.current.id !== conversationId) {
      startedAtRef.current = { id: conversationId, at: Date.now() };
      failuresRef.current = 0;
    }

    let cancelled = false;

    const tick = () => {
      const started = startedAtRef.current;
      if (started && Date.now() - started.at > POLL_TIMEOUT_MS) {
        dispatch(pollTimedOut());
        return;
      }
      dispatch(loadConversation(conversationId))
        .unwrap()
        .then(() => {
          if (!cancelled) failuresRef.current = 0;
        })
        .catch(() => {
          if (cancelled) return;
          failuresRef.current += 1;
          if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) dispatch(pollFailed());
        });
    };

    // Check right away rather than waiting a full interval — matters most on the
    // 409 path, where no fresh load preceded the wait.
    tick();
    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [status, pollingConversationId, dispatch]);
}
